import { useRef, useState } from "react";
import { ApiError } from "@shared-core/data/api/errors";
import { useCustomerBasketAdapter } from "@shared-core/data/CustomerBasketAdapterContext";
import {
  addressFormFailure,
  pickerAddressLine,
  type AddressPicker,
  type PickerAddress,
} from "@shared-core/domain/customer-address-book";
import { Badge } from "../../../app/components/primitives/badge";
import { Button } from "../../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../app/components/primitives/dialog";
import { AddAddressForm } from "./AddAddressForm";

/** What the dialog opens on: the list of addresses, or straight on the form that adds one. */
export type AddressBookView = "list" | "add";

/**
 * The delivery addresses in a modal ("Delivery address"): each as a row to choose by clicking it, and, for the person's
 * own address book and where `canManage`, each row's own actions (make or clear the default, remove) and the way to add
 * one. Choosing closes it. Without `canManage` (the customer's own list, or a split delivery's row) it is only a list to
 * choose from.
 */
export function AddressBookDialog({
  open,
  onOpenChange,
  startOn,
  picker,
  chosenId,
  canManage,
  onChoose,
  onRemoved,
  reload,
  returnFocusTo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startOn: AddressBookView;
  picker: AddressPicker;
  chosenId: string | null;
  canManage: boolean;
  onChoose: (addressId: string) => void;
  /** An address left the book: the page moves its choice off it if it was the one chosen. */
  onRemoved: (addressId: string) => void;
  /** Reads the addresses again after any change to the book. */
  reload: () => void;
  /** What opened it: focus goes back there on close. */
  returnFocusTo: React.RefObject<HTMLElement | null>;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={contentRef}
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          // Start on the chosen address rather than the first control.
          const chosen = contentRef.current?.querySelector<HTMLElement>(
            '[aria-current="true"]',
          );
          if (chosen) {
            event.preventDefault();
            chosen.focus();
          }
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusTo.current?.focus();
        }}
      >
        <AddressBookContent
          startOn={startOn}
          picker={picker}
          chosenId={chosenId}
          canManage={canManage}
          onChoose={onChoose}
          onRemoved={onRemoved}
          reload={reload}
          close={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function AddressBookContent({
  startOn,
  picker,
  chosenId,
  canManage,
  onChoose,
  onRemoved,
  reload,
  close,
}: {
  startOn: AddressBookView;
  picker: AddressPicker;
  chosenId: string | null;
  canManage: boolean;
  onChoose: (addressId: string) => void;
  onRemoved: (addressId: string) => void;
  reload: () => void;
  close: () => void;
}) {
  const baskets = useCustomerBasketAdapter();
  const [view, setView] = useState<AddressBookView>(startOn);
  const isBook = picker.source === "address_book";
  const addUrl = canManage && isBook ? picker.add_url : null;

  if (view === "add" && addUrl !== null) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Delivery address</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <AddAddressForm
            countries={picker.countries}
            findAddresses={
              picker.postcode_lookup_url
                ? (postcode) =>
                    baskets.findAddressesByPostcode(
                      picker.postcode_lookup_url!,
                      { postcode },
                    )
                : null
            }
            onSubmit={(request) => baskets.addAddressToBook(addUrl, request)}
            onAdded={(added) => {
              onChoose(added.address_id);
              reload();
              close();
            }}
            // Opened straight on the form (an empty book): there is no list to go back to.
            onCancel={() => (startOn === "add" ? close() : setView("list"))}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Delivery address</DialogTitle>
      </DialogHeader>
      <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto">
        {picker.addresses.map((address) => (
          <AddressRow
            key={address.address_id}
            address={address}
            isBook={isBook}
            isChosen={address.address_id === chosenId}
            canManage={canManage}
            onChoose={() => {
              onChoose(address.address_id);
              close();
            }}
            onRemoved={() => onRemoved(address.address_id)}
            reload={reload}
          />
        ))}
      </ul>
      {addUrl !== null && (
        <Button variant="outline" onClick={() => setView("add")}>
          Add an address
        </Button>
      )}
    </>
  );
}

function defaultChangeFailure(err: unknown): string {
  return err instanceof ApiError && err.status < 500 && err.detail
    ? err.detail
    : "We couldn't change your default address just now. Please try again.";
}

/** One address: click it to choose it. A book address carries its own actions, and what goes wrong with one is said on its row. */
function AddressRow({
  address,
  isBook,
  isChosen,
  canManage,
  onChoose,
  onRemoved,
  reload,
}: {
  address: PickerAddress;
  isBook: boolean;
  isChosen: boolean;
  canManage: boolean;
  onChoose: () => void;
  onRemoved: () => void;
  reload: () => void;
}) {
  const baskets = useCustomerBasketAdapter();
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const line = pickerAddressLine(address);
  const isDefault = isBook && address.is_default;

  // One default per person, so making one clears the previous: the server keeps that, and the read after shows it.
  async function changeDefault(makeDefault: boolean) {
    if (!address.update_url) return;
    setFailure(null);
    setIsBusy(true);
    try {
      await baskets.setBookAddressDefault(address.update_url, makeDefault);
      reload();
    } catch (err) {
      setFailure(defaultChangeFailure(err));
    } finally {
      setIsBusy(false);
    }
  }

  async function remove() {
    if (!address.remove_url) return;
    setFailure(null);
    setIsBusy(true);
    try {
      await baskets.removeAddressFromBook(address.remove_url);
      onRemoved();
      reload();
    } catch (err) {
      setFailure(addressFormFailure(err).summary);
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <li
      aria-label={address.name}
      className={`rounded-md border p-3 ${isChosen ? "border-primary bg-primary/5" : ""}`}
    >
      <button
        type="button"
        aria-current={isChosen ? "true" : undefined}
        onClick={onChoose}
        className="focus-visible:ring-ring/50 flex w-full items-start gap-3 rounded-sm text-left outline-none focus-visible:ring-[3px]"
      >
        <span
          aria-hidden
          className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border ${isChosen ? "border-primary" : ""}`}
        >
          {isChosen && <span className="bg-primary size-2 rounded-full" />}
        </span>
        <span className="min-w-0 flex-1 text-sm">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium">{address.name}</span>
            {isDefault && <Badge variant="secondary">Default</Badge>}
          </span>
          {line && <span className="text-muted-foreground block">{line}</span>}
          <span className="text-muted-foreground block">
            {address.postcode}
          </span>
        </span>
      </button>
      {canManage && isBook && (address.update_url || address.remove_url) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 pl-7 text-sm">
          {isConfirmingRemoval ? (
            <span className="flex flex-wrap items-center gap-2">
              Remove {address.name} from your address book?
              <Button
                size="sm"
                variant="outline"
                disabled={isBusy}
                onClick={() => void remove()}
              >
                Remove
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={isBusy}
                onClick={() => setIsConfirmingRemoval(false)}
              >
                Keep
              </Button>
            </span>
          ) : (
            <>
              {address.update_url && (
                <Button
                  variant="link"
                  className="h-auto px-0"
                  disabled={isBusy}
                  onClick={() => void changeDefault(!address.is_default)}
                >
                  {address.is_default ? "Clear default" : "Make default"}
                </Button>
              )}
              {address.remove_url && (
                <Button
                  variant="link"
                  className="h-auto px-0"
                  disabled={isBusy}
                  onClick={() => {
                    setFailure(null);
                    setIsConfirmingRemoval(true);
                  }}
                >
                  Remove
                </Button>
              )}
            </>
          )}
        </div>
      )}
      {failure && (
        <p
          role="alert"
          className="mt-2 pl-7 text-sm text-red-700 dark:text-red-400"
        >
          {failure}
        </p>
      )}
    </li>
  );
}
